import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  Check, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  AlertCircle
} from 'lucide-react';
import '../styles/UserAccessControl.css';

const UserAccessControl = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null); // Track which cell is being updated
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('https://gnrl-bknd-server.onrender.com/api/users');
      setUsers(response.data.map(user => ({
        ...user,
        accessMonths: user.accessMonths || {}
      })));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
      setLoading(false);
    }
  };

  const toggleAccess = async (userId, month, year) => {
    const updateKey = `${userId}-${month}-${year}`;
    setUpdating(updateKey);
    try {
      const response = await axios.post('https://gnrl-bknd-server.onrender.com/api/users/access', {
        userId,
        month,
        year,
        enabled: !getUserAccess(userId, month, year)
      });

      setUsers(users.map(user => 
        user._id === userId 
          ? { ...user, accessMonths: response.data.accessMonths }
          : user
      ));
    } catch (error) {
      console.error('Error updating access:', error);
      setError('Failed to update access. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const getUserAccess = (userId, month, year) => {
    const user = users.find(u => u._id === userId);
    return user?.accessMonths?.[`${year}-${month}`] || false;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        Loading user data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <AlertCircle />
        {error}
      </div>
    );
  }

  return (
    <div className="user-access-control">
      <h1>User Access Management</h1>
      
      <div className="year-selector">
        <button 
          onClick={() => setSelectedYear(selectedYear - 1)}
          className="year-nav"
          aria-label="Previous year"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="current-year">{selectedYear}</span>
        <button 
          onClick={() => setSelectedYear(selectedYear + 1)}
          className="year-nav"
          aria-label="Next year"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="users-table">
        <div className="table-header">
          <div className="user-info">User Details</div>
          {months.map(month => (
            <div key={month} className="month-header">
              {month.substring(0, 3)}
            </div>
          ))}
        </div>

        {users.map(user => (
          <div key={user._id} className="user-row">
            <div className="user-details">
              <div className="user-name">
                <User className="icon" size={16} />
                <span>{user.username}</span>
              </div>
              <div className="user-email">
                <Mail className="icon" size={16} />
                <span>{user.email}</span>
              </div>
              {user.phone && (
                <div className="user-phone">
                  <Phone className="icon" size={16} />
                  <span>{user.phone}</span>
                </div>
              )}
            </div>

            {months.map((month, index) => {
              const isUpdating = updating === `${user._id}-${index}-${selectedYear}`;
              const hasAccess = getUserAccess(user._id, index, selectedYear);
              
              return (
                <div 
                  key={month} 
                  className={`access-cell ${hasAccess ? 'enabled' : 'disabled'}`}
                  onClick={() => !isUpdating && toggleAccess(user._id, index, selectedYear)}
                  style={{ cursor: isUpdating ? 'wait' : 'pointer' }}
                >
                  {isUpdating ? (
                    <Loader2 className="icon" size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : hasAccess ? (
                    <Check className="icon" size={18} />
                  ) : (
                    <X className="icon" size={18} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserAccessControl; 
